import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    Button,
    Icon,
    ImageHoverPreview,
    SearchBar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    transitions,
} from "@elit-hair/elit-ui";
import * as Sentry from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCheck, TbEdit, TbPlus, TbTrash, TbX } from "react-icons/tb";
import { toast } from "sonner";

import { TreatmentModal } from "@/components/treatment-modal";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PERMISSIONS } from "@/constants/permissions";
import { useCreateTreatment, useDeleteTreatment, useTreatments, useUpdateTreatment } from "@/queries/treatment.queries";
import type { CreateTreatmentRequest, Treatment, TreatmentRowProps } from "@/types/treatment.types";
import { fadeInUp } from "@/utils/animations";
import { handleErrorToast } from "@/utils/error-handler";

const TreatmentRow = ({
    treatment,
    onEdit,
    onDelete,
    isDeleting,
    canEdit = true,
    canDelete = true,
}: TreatmentRowProps) => {
    const { t } = useTranslation();

    return (
        <TableRow>
            <TableCell>
                <div className="flex items-center gap-3">
                    {treatment.image && (
                        <ImageHoverPreview
                            src={treatment.image}
                            alt={treatment.title}
                            thumbnailSize="md"
                            previewSize={256}
                        />
                    )}
                    <span className="font-medium">{treatment.title}</span>
                </div>
            </TableCell>
            <TableCell>{treatment.brand}</TableCell>
            <TableCell>{treatment.category}</TableCell>
            <TableCell>
                {treatment.duration} {t("min")}
            </TableCell>
            <TableCell>
                <div className="flex items-center justify-center">
                    {treatment.isAppHighlighted ? (
                        <Icon icon={TbCheck} className="text-green-600" />
                    ) : (
                        <Icon icon={TbX} className="text-gray-400" />
                    )}
                </div>
            </TableCell>
            <TableCell>
                {(canEdit || canDelete) && (
                    <div className="flex gap-2">
                        {canEdit && (
                            <Button variant="outline" size="icon" onClick={onEdit}>
                                <Icon icon={TbEdit} />
                            </Button>
                        )}
                        {canDelete && (
                            <Button variant="outline" size="icon" onClick={onDelete} disabled={isDeleting}>
                                <Icon icon={TbTrash} />
                            </Button>
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

export const TreatmentsPage = () => {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState<boolean>(false);
    const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [treatmentToDelete, setTreatmentToDelete] = useState<string | null>(null);

    const canCreate = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_TREATMENT_CREATE]);
    const canEdit = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_TREATMENT_UPDATE]);
    const canDelete = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_TREATMENT_DELETE]);

    // Fetch treatments data
    const {
        data: treatmentsData,
        isLoading,
        error,
    } = useTreatments({
        search: searchQuery || undefined,
    });

    // Mutations
    const deleteTreatmentMutation = useDeleteTreatment();
    const createTreatmentMutation = useCreateTreatment();
    const updateTreatmentMutation = useUpdateTreatment();

    const handleOpenAddTreatment = () => {
        setEditingTreatment(null);
        setIsTreatmentModalOpen(true);
    };

    const handleOpenEditTreatment = (treatment: Treatment) => {
        setEditingTreatment(treatment);
        setIsTreatmentModalOpen(true);
    };

    const handleCloseTreatmentModal = () => {
        setIsTreatmentModalOpen(false);
        setEditingTreatment(null);
    };

    const handleSubmitTreatment = async (data: CreateTreatmentRequest) => {
        try {
            if (editingTreatment) {
                await updateTreatmentMutation.mutateAsync({
                    id: editingTreatment.id,
                    data,
                });
                toast.success(t("treatment_updated_successfully"));
            } else {
                await createTreatmentMutation.mutateAsync(data);
                toast.success(t("treatment_created_successfully"));
            }
        } catch (error) {
            const isUpdate = !!editingTreatment;
            handleErrorToast(error, {
                errorMessage: isUpdate ? t("failed_to_update_treatment") : t("failed_to_create_treatment"),
                permissionDeniedDescription: isUpdate
                    ? t("permission_denied_update_treatment_description")
                    : t("permission_denied_create_treatment_description"),
                unauthorizedMessage: t("unauthorized_access"),
                permissionDeniedMessage: t("permission_denied"),
            });
            throw error;
        }
    };

    const handleDeleteTreatment = (treatmentId: string) => {
        setTreatmentToDelete(treatmentId);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteTreatment = async () => {
        if (!treatmentToDelete) return;

        try {
            await deleteTreatmentMutation.mutateAsync(treatmentToDelete);
            toast.success(t("treatment_deleted_successfully"));
            setDeleteDialogOpen(false);
            setTreatmentToDelete(null);
        } catch (error) {
            handleErrorToast(error, {
                errorMessage: t("failed_to_delete_treatment"),
                permissionDeniedDescription: t("permission_denied_delete_treatment_description"),
                unauthorizedMessage: t("unauthorized_access"),
                permissionDeniedMessage: t("permission_denied"),
            });
            Sentry.captureException(error, {
                tags: {
                    component: "TreatmentsPage",
                    action: "deleteTreatment",
                },
            });
        }
    };

    return (
        <div className="min-h-screen p-4">
            <motion.div {...fadeInUp} transition={transitions.slow}>
                <div className="flex justify-end gap-2">
                    <SearchBar
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                        debounceMs={300}
                        placeholder={t("search_treatments")}
                        className="w-96 md:w-[300px]"
                    />
                    {canCreate && (
                        <Button variant="default" onClick={handleOpenAddTreatment}>
                            <Icon icon={TbPlus} />
                            {t("add_treatment")}
                        </Button>
                    )}
                </div>
                <div className="rounded-md">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("title")}</TableHead>
                                <TableHead>{t("brand")}</TableHead>
                                <TableHead>{t("category")}</TableHead>
                                <TableHead>{t("duration")}</TableHead>
                                <TableHead className="text-center">{t("app_highlighted")}</TableHead>
                                <TableHead>{t("actions")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell className="text-center py-8">{t("loading")}...</TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell className="text-center py-8 text-red-500">
                                        {t("error_loading_treatments")}
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            ) : !treatmentsData?.data?.length ? (
                                <TableRow>
                                    <TableCell className="text-center py-8">
                                        {searchQuery ? t("no_treatments_found") : t("no_treatments")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                treatmentsData.data.map((treatment) => (
                                    <TreatmentRow
                                        key={treatment.id}
                                        treatment={treatment}
                                        onEdit={() => handleOpenEditTreatment(treatment)}
                                        onDelete={() => handleDeleteTreatment(treatment.id)}
                                        isDeleting={deleteTreatmentMutation.isPending}
                                        canEdit={canEdit}
                                        canDelete={canDelete}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </motion.div>
            <TreatmentModal
                isOpen={isTreatmentModalOpen}
                onClose={handleCloseTreatmentModal}
                treatment={editingTreatment}
                onSubmit={handleSubmitTreatment}
            />
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("this_action_cannot_be_undone")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteTreatmentMutation.isPending}>
                            {t("cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteTreatment}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteTreatmentMutation.isPending}
                        >
                            {deleteTreatmentMutation.isPending ? t("deleting") : t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export const Route = createFileRoute("/Treatments/")({
    component: TreatmentsPage,
});
